import { ReactNode } from 'react';
import { Rss, Wallet } from 'lucide-react';

import {
  AnalyticsIcon,
  DocumentIcon,
  HelpIcon,
  MembersIcon,
  SettingsIcon,
} from 'components/icons';
import { LightningIcon, MembersSidebarIcon, InfoIcon, MagicSearchStarsIcon } from 'components/icons/v2';
import { ConnectionsIcon } from 'components/icons/v2/ConnectionsIcon';

export interface IRoutes {
  name: string;
  icon?: ReactNode;
  title?: string;
  getPath: (...data: string[]) => string;
  resolve: (...data: string[]) => boolean | string;
}

export type RouteType =
  'HOME'
  | 'SIGN_OUT'
  | 'LOGIN'
  | 'REGISTER'
  | 'MEMBER_PROFILE'
  | 'UP'
  | 'MEMBER_EDIT'
  | 'MEMBER_EDIT_SOCIAL'
  | 'MEMBER_EDIT_SKILLS'
  | 'MEMBER_EDIT_HOBBY'
  | 'MEMBER_EDIT_LOCATION'
  | 'MEMBER_EDIT_EXPERIENCE_ADD'
  | 'MEMBER_EDIT_EXPERIENCE_EDIT'
  | 'MEMBER_EDIT_FEATURED_ADD'
  | 'MEMBER_EDIT_FEATURED_EDIT'
  | 'MEMBER_EDIT_EDUCATION_ADD'
  | 'MEMBER_EDIT_EDUCATION_EDIT'
  | 'MEMBER_COMPANY'
  | 'MEMBER_COMPANY_ADD'
  | 'MEMBER_POSITION'
  | 'MEMBER_QUALIFICATION'
  | 'MEMBER_UNIVERSITY'
  | 'MEMBERS'
  | 'REQUESTS'
  | 'OAUTH'
  | 'TWA'
  | 'PANEL'
  | 'ANALYTICS'
  | 'REACH'
  | 'HELP'
  | 'LANDING_BUILDER'
  | 'SETTINGS'
  | 'APPLY'
  | 'CREATE_COMMUNITY'
  | 'SELECT_GOAL'
  | 'DESCRIBE'
  | 'RULES'
  | 'START'
  | 'BLOG'
  | 'STAT_MEMBERS'
  | 'PAYMENTS'
  | 'RESET_PASSWORD'
  | 'FORGOT_PASSWORD'
  | 'OPPORTUNITIES'
  | 'DEALS'
  | 'DEAL'
  | 'CONNECTIONS'
  | 'APPLICATION'
  | 'DISCOVERY'
  | 'SPACE_SETTINGS'
  | 'SPACE'
  | 'WIN_WIN'
    // ONBOARDING 180
  | 'ONB_180_BASIC'
  | 'ONB_180_ABOUT'
  | 'ONB_180_DREAMS'
  | 'ONB_180_UP_TO'
  | 'ONB_180_HOBBIES'
  | 'ONB_180_PROUD_OF';

export type MemberRouteType =
  'SUBSCRIPTION'
  | 'MEMBERS'
  | 'CONNECTIONS'
  | 'OPPORTUNITIES'
  | 'DEALS'
  | 'DEAL'
  | 'MEMBER_PROFILE'
  | 'WELCOME'
  | 'WELCOME_LOADING'
  | 'MY_PROFILE'
  | 'EDIT_PROFILE'
  | 'DISCOVERY'
  | 'UP'
  | 'SPACE'
  | 'REFERRAL_INVITE'
  | 'WIN_WIN'
  // ONBOARDING 180
  | 'ONB_180_BASIC'
  | 'ONB_180_ABOUT'
  | 'ONB_180_DREAMS'
  | 'ONB_180_UP_TO'
  | 'ONB_180_HOBBIES'
  | 'ONB_180_PROUD_OF';


const HOME: IRoutes = {
  name: 'home',
  getPath: () => '/',
  resolve: (url: string) => url === OWNER_ROUTES.HOME.getPath()
};
const SIGN_OUT: IRoutes = {
  name: 'sign-out',
  getPath: () => '/sign-out',
  resolve: (url: string) => url === OWNER_ROUTES.SIGN_OUT.getPath()
};
const LOGIN: IRoutes = {
  name: 'login',
  getPath: () => '/login',
  resolve: (url: string) => url === OWNER_ROUTES.LOGIN.getPath()
};
const REGISTER: IRoutes = {
  name: 'register',
  getPath: () => '/register',
  resolve: (url: string) => url === OWNER_ROUTES.REGISTER.getPath()
};
const MEMBER_PROFILE: IRoutes = {
  name: 'member-profile',
  title: 'Member Profile',
  getPath: (id: string | number) => `/${id}`,
  resolve: (url: string) => {
    const match = url.match(/^\/(\d+)(\?|$)/);
    if (match) return true
    return url.startsWith('/members/')
  },
};
const UP: IRoutes = {
  name: 'up',
  getPath: () => `/up`,
  resolve: (url: string) => url.startsWith('/up')
};
const MEMBER_EDIT: IRoutes = {
  name: 'member-profile-edit',
  getPath: (id: string) => `/members/${id}/edit`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_SOCIAL: IRoutes = {
  name: 'member-profile-social-edit',
  getPath: (id: string) => `/members/${id}/edit/social`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_SKILLS: IRoutes = {
  name: 'member-profile-skils-edit',
  getPath: (id: string) => `/members/${id}/edit/skills`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_HOBBY: IRoutes = {
  name: 'member-profile-hobby-edit',
  getPath: (id: string) => `/members/${id}/edit/hobby`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_LOCATION: IRoutes = {
  name: 'member-profile-location-edit',
  getPath: (id: string) => `/members/${id}/edit/location`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_EXPERIENCE_ADD: IRoutes = {
  name: 'member-profile-experience-add',
  getPath: (id: string) => `/members/${id}/edit/experience/add`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_EXPERIENCE_EDIT: IRoutes = {
  name: 'member-profile-experience-edit',
  getPath: (id: string, experienceId: string) =>
    `/members/${id}/edit/experience/${experienceId}/edit`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_FEATURED_ADD: IRoutes = {
  name: 'member-profile-featured-add',
  getPath: (id: string) => `/members/${id}/edit/featured/add`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_FEATURED_EDIT: IRoutes = {
  name: 'member-profile-featured-edit',
  getPath: (id: string, featuredId: string) =>
    `/members/${id}/edit/featured/${featuredId}/edit`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_EDIT_EDUCATION_ADD: IRoutes = {
  name: 'member-profile-education-add',
  getPath: (id: string) => `/members/${id}/edit/education/add`,
  resolve: (url: string) => url.endsWith('/edit/education/add')
};
const MEMBER_EDIT_EDUCATION_EDIT: IRoutes = {
  name: 'member-profile-education-edit',
  getPath: (id: string, educationId: string) =>
    `/members/${id}/edit/education/${educationId}/edit`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_COMPANY: IRoutes = {
  name: 'member-profile-company',
  getPath: (id: string) => `/members/${id}/company`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_COMPANY_ADD: IRoutes = {
  name: 'member-profile-company-add',
  getPath: (id: string) => `/members/${id}/company/add`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_POSITION: IRoutes = {
  name: 'member-profile-position',
  getPath: (id: string) => `/members/${id}/position`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_QUALIFICATION: IRoutes = {
  name: 'member-profile-qualification',
  getPath: (id: string) => `/members/${id}/qualification`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBER_UNIVERSITY: IRoutes = {
  name: 'member-profile-university',
  getPath: (id: string) => `/members/${id}/university`,
  resolve: (url: string) => url.startsWith('/members/')
};
const MEMBERS: IRoutes = {
  name: 'members',
  title: 'Members',
  getPath: () => '/members',
  resolve: (url: string) => url === OWNER_ROUTES.MEMBERS.getPath(),
  icon: <MembersSidebarIcon />,
};
const REQUESTS: IRoutes = {
  name: 'requests',
  getPath: () => '/requests',
  resolve: (url: string) => url === OWNER_ROUTES.REQUESTS.getPath()
};
const OAUTH: IRoutes = {
  name: 'oauth-complete',
  getPath: () => '/i/oauth-complete',
  resolve: (url: string) => url === OWNER_ROUTES.OAUTH.getPath()
};
const TWA: IRoutes = {
  name: 'telegram-web-app',
  getPath: () => '/twa',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.TWA.getPath())
};
const PANEL: IRoutes = {
  name: 'panel',
  title: 'Applications',
  icon: <MembersIcon width={24} height={24} />,
  getPath: () => '/panel',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.PANEL.getPath())
};
const ANALYTICS: IRoutes = {
  name: 'analytics',
  getPath: () => '/analytics',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.ANALYTICS.getPath()),
  title: 'Analytics',
  icon: <AnalyticsIcon width={24} height={24} />
};
const REACH: IRoutes = {
  getPath: () => '/reach',
  name: 'reach',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.REACH.getPath()),
  title: 'Reach out'
};
const HELP: IRoutes = {
  name: 'help',
  icon: <HelpIcon width={'24'} height={'24'} />,
  title: 'Get help',
  getPath: () => '/communityInfo',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.HELP.getPath())
};
const LANDING_BUILDER: IRoutes = {
  name: 'landing',
  title: 'Landing',
  getPath: () => '/landing',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.LANDING_BUILDER.getPath()),
  icon: <DocumentIcon />
};
const SETTINGS: IRoutes = {
  name: 'settings',
  title: 'Settings',
  getPath: () => '/settings',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.SETTINGS.getPath()),
  icon: <SettingsIcon width={24} height={24} />
};
const APPLY: IRoutes = {
  name: 'apply',
  getPath: () => '/apply',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.APPLY.getPath())
};
const CREATE_COMMUNITY: IRoutes = {
  name: 'create-community',
  getPath: () => '/create-community',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.CREATE_COMMUNITY.getPath())
};
const SELECT_GOAL: IRoutes = {
  name: 'goal',
  getPath: () => '/goal',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.SELECT_GOAL.getPath())
};
const DESCRIBE: IRoutes = {
  name: 'describe',
  getPath: () => '/describe',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.DESCRIBE.getPath())
};
const RULES: IRoutes = {
  name: 'rules',
  getPath: () => '/rules',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.RULES.getPath())
};
const START: IRoutes = {
  name: 'start',
  getPath: () => '/start',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.START.getPath())
};
const STAT_MEMBERS: IRoutes = {
  name: 'stat_members',
  getPath: () => '/statmembers',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.STAT_MEMBERS.getPath())
};
const BLOG: IRoutes = {
  name: 'blog',
  title: 'Blog',
  getPath: () => '/blog',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.BLOG.getPath()),
  icon: <Rss />
};
const PAYMENTS: IRoutes = {
  name: 'payments',
  icon: <Wallet />,
  title: 'Payments',
  getPath: () => '/payments',
  resolve: (url: string) => url.startsWith(OWNER_ROUTES.PAYMENTS.getPath())
};

const SUBSCRIPTION: IRoutes = {
  name: 'subscription',
  title: 'Subscription',
  getPath: () => '/subscription',
  resolve: (url: string) => url.startsWith('/subscription'),
  icon: <Rss />
}

const OPPORTUNITIES: IRoutes = {
  name: 'opportunities',
  title: 'Opportunities',
  getPath: () => '/opportunities',
  resolve: (url: string) => url.startsWith('/opportunities'),
  icon: <LightningIcon height={20} />
}

const DEALS: IRoutes = {
  name: 'deals',
  title: 'Deals',
  getPath: () => '/deals',
  resolve: (url: string) => url === '/deals' || url.startsWith('/deals?'),
  icon: <LightningIcon height={20} />
}

const DEAL: IRoutes = {
  name: 'deal',
  title: 'Deal',
  getPath: (id: string) => `/deals/${id}`,
  resolve: (url: string) => url.startsWith('/deals/'),
}

const CONNECTIONS: IRoutes = {
  name: 'connections',
  title: 'Connections',
  getPath: () => '/connections',
  resolve: (url: string) => url.startsWith('/connections'),
  icon: <ConnectionsIcon />
}

const WELCOME: IRoutes = {
  name: 'welcome',
  getPath: () => '/welcome',
  resolve: (url: string) => url.startsWith('/welcome')
};

const WELCOME_LOADING: IRoutes = {
  name: 'welcome',
  getPath: () => '/welcome/loading',
  resolve: (url: string) => url.includes('/welcome/loading'),
};

const MY_PROFILE: IRoutes = {
  name: 'my-profile',
  getPath: () => '/my-profile',
  resolve: (url: string) => url.startsWith('/my-profile'),
};

const EDIT_PROFILE: IRoutes = {
  name: 'edit-profile',
  getPath: () => '/my-profile/edit',
  resolve: (url: string) => url.startsWith('/my-profile/edit'),
};

const RESET_PASSWORD: IRoutes = {
  name: 'reset-password',
  getPath: () => '/reset-password',
  resolve: (url: string) => url.startsWith('/reset-password'),
};

const FORGOT_PASSWORD: IRoutes = {
  name: 'forgot-password',
  getPath: () => '/forgot-password',
  resolve: (url: string) => url.startsWith('/forgot-password'),
}

const APPLICATION: IRoutes = {
  name: 'application',
  title: 'Application',
  getPath: (id: string) => `/application/${id}`,
  resolve: (url: string) => {
    const applicationRegex = /^\/application\/\d+$/;
    return applicationRegex.test(url);
  }
};

const DISCOVERY: IRoutes = {
  name: 'discovery',
  title: 'Discovery spaces',
  getPath: () => `/discovery`,
  resolve: (url: string) => url.startsWith('/discovery'),
};

const SPACE: IRoutes = {
  name: 'space',
  title: 'About',
  getPath: (subdomain: string) => `/space/${subdomain}`,
  resolve: (url: string) => {
    const spaceRegex = /^\/space\/[^\/]+$/;
    return spaceRegex.test(url);
  },
  icon: <InfoIcon />,
};

const SPACE_SETTINGS: IRoutes = {
  name: 'space_settings',
  getPath: (subdomain: string) => `/space/${subdomain}/settings`,
  resolve: (url: string) => {
    const spaceSettingsRegex = /^\/space\/\w+\/settings$/;
    return spaceSettingsRegex.test(url);
  }
};

const REFERRAL_INVITE: IRoutes = {
  name: 'refferal_invite',
  getPath: (inviteHash: string) => `/r/${inviteHash}`,
  resolve: (url: string) => {
    const referralInviteRegex = /^\/r\/\w+$/;
    return referralInviteRegex.test(url);
  }
};

const WIN_WIN: IRoutes = {
  name: 'win_win',
  title: 'Win-Win',
  getPath: () => `/win-win`,
  resolve: (url: string) => url.startsWith('/win-win'),
  icon: <MagicSearchStarsIcon />
};

const ONB_180_BASIC: IRoutes = {
  name: 'onb_180_basic',
  title: 'Let\'s with intro',
  getPath: () => `/o/basic`,
  resolve: (url: string) => url.startsWith('/o/basic')
};

const ONB_180_ABOUT: IRoutes = {
  name: 'onb_180_about',
  title: 'About',
  getPath: () => `/o/about`,
  resolve: (url: string) => url.startsWith('/o/about')
};

const ONB_180_DREAMS: IRoutes = {
  name: 'onb_180_dreams',
  title: 'Dreams',
  getPath: () => `/o/dreams`,
  resolve: (url: string) => url.startsWith('/o/dreams')
};

const ONB_180_UP_TO: IRoutes = {
  name: 'onb_180_up_to',
  title: 'Up to',
  getPath: () => `/o/up-to`,
  resolve: (url: string) => url.startsWith('/o/up-to')
};

const ONB_180_HOBBIES: IRoutes = {
  name: 'onb_180_hobbies',
  title: 'Hobbies',
  getPath: () => `/o/hobbies`,
  resolve: (url: string) => url.startsWith('/o/hobbies')
};

const ONB_180_PROUD_OF: IRoutes = {
  name: 'onb_180_proud_of',
  title: 'Proud of',
  getPath: () => `/o/proud-of`,
  resolve: (url: string) => url.startsWith('/o/proud-of')
};

export const OWNER_ROUTES: Record<RouteType, IRoutes> = {
  HOME,
  SIGN_OUT,
  LOGIN,
  REGISTER,
  UP,
  MEMBER_PROFILE,
  MEMBER_EDIT,
  MEMBER_EDIT_SOCIAL,
  MEMBER_EDIT_SKILLS,
  MEMBER_EDIT_HOBBY,
  MEMBER_EDIT_LOCATION,
  MEMBER_EDIT_EXPERIENCE_ADD,
  MEMBER_EDIT_EXPERIENCE_EDIT,
  MEMBER_EDIT_FEATURED_ADD,
  MEMBER_EDIT_FEATURED_EDIT,
  MEMBER_EDIT_EDUCATION_ADD,
  MEMBER_EDIT_EDUCATION_EDIT,
  MEMBER_COMPANY,
  MEMBER_COMPANY_ADD,
  MEMBER_POSITION,
  MEMBER_QUALIFICATION,
  MEMBER_UNIVERSITY,
  MEMBERS,
  REQUESTS,
  OAUTH,
  TWA,
  PANEL,
  ANALYTICS,
  REACH,
  HELP,
  LANDING_BUILDER,
  SETTINGS,
  APPLY,
  CREATE_COMMUNITY,
  SELECT_GOAL,
  DESCRIBE,
  RULES,
  START,
  BLOG,
  PAYMENTS,
  RESET_PASSWORD,
  FORGOT_PASSWORD,
  OPPORTUNITIES,
  DEALS,
  DEAL,
  CONNECTIONS,
  APPLICATION,
  STAT_MEMBERS,
  DISCOVERY,
  SPACE,
  SPACE_SETTINGS,
  WIN_WIN,
  ONB_180_BASIC,
  ONB_180_ABOUT,
  ONB_180_HOBBIES,
  ONB_180_PROUD_OF,
  ONB_180_DREAMS,
  ONB_180_UP_TO
};

export const MEMBER_ROUTES: Record<MemberRouteType, IRoutes> = {
  OPPORTUNITIES,
  DEALS,
  DEAL,
  CONNECTIONS,
  SUBSCRIPTION,
  MEMBERS,
  MEMBER_PROFILE,
  WELCOME,
  WELCOME_LOADING,
  MY_PROFILE,
  EDIT_PROFILE,
  SPACE,
  DISCOVERY,
  UP,
  REFERRAL_INVITE,
  WIN_WIN,
  ONB_180_BASIC,
  ONB_180_ABOUT,
  ONB_180_HOBBIES,
  ONB_180_PROUD_OF,
  ONB_180_DREAMS,
  ONB_180_UP_TO,
};

export interface IRouteGroup {
  routeGroupName: string;
  routes: IRoutes[];
}

export const resolveRoute = (url: string) => {
  return [...Object.values(OWNER_ROUTES), ...Object.values(MEMBER_ROUTES)].find((route) => route.resolve(url));
};

export const getRouteSearchParams = (url: string): Record<string, string> => {
  const params = url.split('?')[1];
  if (!params) return {};

  return params.split('&').reduce((acc: Record<string, string>, item: string) => {
    const [key, value] = item.split('=');
    acc[key] = value;
    return acc;
  }, {});
}

export const buildRoutePath = (url: string, additionalSearchParams: Record<string, string | string[] | undefined>) => {
  const params = { ...getRouteSearchParams(url), ...additionalSearchParams };
  const search = Object.keys(params).map((key) => `${key}=${params[key]}`).join('&');

  return `${url.split('?')[0]}?${search}`;
}
